/**
 * Bringing the browser-session copy of the saved plans into the app's own list (3 Oct 2026).
 *
 * Aged care and Support at Home also write their plans to sessionStorage, and Support at Home writes
 * ONLY there, so the Dashboard reads that copy in. It used to REPLACE the app's list with it. But the
 * village, downsizer, granny flat and cashflow modules save to the app's list alone, and the session
 * copy is whatever aged care last wrote (or what was mirrored when the file was last saved). So
 * opening the Dashboard dropped every plan those four modules had saved since, and the next autosave
 * made the loss permanent.
 *
 * Now the session copy only ADDS plans the app does not already hold. Nothing in the app deletes a
 * plan from its list, so adding is all that was ever needed.
 */
/*
 * ONLY THE MODULES THAT WRITE THE SESSION COPY (21 Oct 2026). Choosing a plan now REPLACES an earlier one
 * (strategy/chosenPlan.ts; one plan per village unit, rvChosenPlan.ts), so "nothing deletes a plan" stopped
 * being true. A stale session copy then brought a replaced village plan back, still marked chosen, beside
 * the new one (caught live). The village, downsizer, granny flat and cashflow modules never write the
 * session copy, so their plans are never taken from it.
 */
const SESSION_PLAN_TYPES = new Set(['aged_care', 'home_care']);

export function mergeSessionPlans<T extends { id?: unknown }>(current: T[], fromSession: unknown): T[] {
    if (!Array.isArray(fromSession)) return current;
    const have = new Set(current.map((p) => String(p?.id)));
    const extra = (fromSession as T[]).filter((p) => p && p.id != null && !have.has(String(p.id))
        && SESSION_PLAN_TYPES.has(String((p as { type?: unknown }).type ?? 'aged_care')));
    return extra.length ? [...current, ...extra] : current;
}
